import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { first } from "lodash-es";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  HealthRecordDetailLaboratory,
  HealthRecordDiagnostic
} from "src/app/backend/models/health-record";
import { MedicalServiceId } from "src/app/backend/models/medical-service";
import {
  ExamTest,
  WorkOrder,
  WorkOrderItem,
  workOrderNeedReconcile,
  WorkOrderStatus
} from "src/app/backend/models/work-order/work-order";
import { WorkOrderCreate } from "src/app/backend/models/work-order/work-order.create";
import { UserNotify } from "src/app/support/notify-decorator";
import { omitUndefinedAndNullProperties } from "src/app/support/object";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { sntTranslate } from "src/app/support/translate";
import { environment } from "src/environments/environment";

@Injectable()
export class WorkOrderBackend {
  private readonly http = inject(HttpClient);

  list(request: PageRequest): Observable<PaginatedResult<WorkOrder>> {
    return this.http
      .get<PaginatedResult<WorkOrder>>(
        `${environment.snoots.api.healthcare}/work-orders`,
        {
          params: omitUndefinedAndNullProperties({
            p: request.page,
            s: request.pageSize,
            ...request.filterValues,
            sort: "desc",
            orderBy: "createdAt"
          })
        }
      )
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(extendDoc)
          };
        })
      );
  }

  search(patientId: string): Observable<PaginatedResult<WorkOrder>> {
    return this.http
      .get<PaginatedResult<WorkOrder>>(
        `${environment.snoots.api.healthcare}/work-orders`,
        {
          params: {
            patientId: patientId || ""
          }
        }
      )
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(extendDoc)
          };
        })
      );
  }

  load(id: string): Observable<WorkOrder> {
    return this.http
      .get<WorkOrder>(`${environment.snoots.api.healthcare}/work-orders/${id}`)
      .pipe(map(extendDoc));
  }

  loadHealthRecordLaboratory(
    id: string
  ): Observable<HealthRecordDetailLaboratory | undefined> {
    return this.http
      .get<PaginatedResult<HealthRecordDetailLaboratory>>(
        `${environment.snoots.api.healthcare}/work-orders/${id}/health-records`
      )
      .pipe(
        map((records) => {
          return first(records.docs);
        })
      );
  }

  loadHealthRecordDiagnostic(
    id: string
  ): Observable<HealthRecordDiagnostic | undefined> {
    return this.http
      .get<PaginatedResult<HealthRecordDiagnostic>>(
        `${environment.snoots.api.healthcare}/work-orders/${id}/health-records`
      )
      .pipe(
        map((records) => {
          return first(records.docs);
        })
      );
  }

  @UserNotify({
    successMessage: marker("Ordine di lavoro creato"),
    errorMessage: marker("Impossibile creare l'ordine di lavoro")
  })
  create(payload: WorkOrderCreate): Observable<WorkOrder> {
    return this.http
      .post<WorkOrder>(
        `${environment.snoots.api.healthcare}/work-orders`,
        payload
      )
      .pipe(map(extendDoc));
  }

  @UserNotify({
    successMessage: marker("Ordine di lavoro aggiornato"),
    errorMessage: marker("Impossibile aggiornare l'ordine di lavoro")
  })
  update(id: string, request: Partial<WorkOrder>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/work-orders/${id}`,
      request
    );
  }

  reconcileItemWithMedicalService({
    id,
    itemId,
    medicalServiceId
  }: {
    id: string;
    itemId: string;
    medicalServiceId: MedicalServiceId;
  }): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/work-orders/${id}/items/${itemId}`,
      {
        medicalServiceId
      }
    );
  }

  @UserNotify({
    successMessage: marker("Ordine di lavoro aggiornato"),
    errorMessage: marker("Impossibile aggiornare l'ordine di lavoro")
  })
  reconcile(id: string, request: Partial<WorkOrder>): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.healthcare}/work-orders/${id}/reconcile`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Ordine di lavoro eliminato"),
    errorMessage: marker("Impossibile eliminare l'ordine di lavoro")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.healthcare}/work-orders/${id}`
    );
  }

  @UserNotify({
    successMessage: (
      id: string,
      examId: string,
      parameterId: string,
      item: Partial<ExamTest>,
      itemName: string
    ) => `${itemName} ${sntTranslate(marker("aggiornato"))}`,
    errorMessage: marker("Impossibile aggiornare la riga")
  })
  updateParameter(
    id: string,
    examId: string,
    parameterId: string,
    item: Partial<ExamTest>
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/work-orders/${id}/exams/${examId}/tests/${parameterId}`,
      item
    );
  }
}

function extendDoc(workOrder: WorkOrder): WorkOrder {
  let status: WorkOrderStatus;

  switch (true) {
    case !!workOrder.canceledAt: {
      status = WorkOrderStatus.DELETED;
      break;
    }
    case !!workOrder.completedAt: {
      status = WorkOrderStatus.COMPLETED;
      break;
    }
    case workOrder.results && workOrder.results?.length > 0: {
      status = WorkOrderStatus.IN_PROGRESS;
      break;
    }
    case !!workOrder.submittedAt: {
      status = WorkOrderStatus.ACCEPTED;
      break;
    }
    case !!workOrder.sentAt: {
      status = WorkOrderStatus.TO_ACCEPT;
      break;
    }
    default: {
      status = WorkOrderStatus.CREATED;
      break;
    }
  }

  return {
    ...workOrder,
    status,
    needToBeReconciliated: workOrderNeedReconcile(workOrder),
    description:
      workOrder.description || buildWorkOrderDescriptionFromItems(workOrder)
  };
}

function buildWorkOrderDescriptionFromItems(workOrder: WorkOrder): string {
  if (!workOrder.items) {
    return "";
  }

  return workOrder.items
    .map((item: WorkOrderItem) => {
      return item.code.replaceAll("_", " ");
    })
    .join(", ");
}
